import React, { useState, useEffect, useCallback } from "react"
import {
    Container, Grid, Typography, TextField, Button, List, ListItem, ListItemButton,
    ListItemText, IconButton, FormControl, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent,
    DialogActions, Table, TableBody, TableCell, TableHead, TableRow, InputAdornment
} from "@mui/material"
import Stack from '@mui/material/Stack'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import PostAddIcon from '@mui/icons-material/PostAdd'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs"
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers-pro"
import dayjs from 'dayjs'
import {
    GetBillingGroups, CreateBillingGroup, UpdateBillingGroup, DeleteBillingGroup,
    CreateBillingCode, DeleteBillingCode, GetBillingPricing, CreateBillingPricing,
    DeleteBillingPricing, GetOrgBilling, UpdateOrgBillingOrder,
    GetPriorities, CreatePriority, UpdatePriority, DeletePriority, ReorderPriorities,
    GetPriorityPricing, CreatePriorityPricing, DeletePriorityPricing
} from "../../../../api"
import { useNavigate } from 'react-router-dom'

const BillingSettings = ({ user, access }) => {
    const [groups, setGroups] = useState([])
    const [billing, setBilling] = useState([])
    const [pricing, setPricing] = useState([])
    const [selectedBill, setSelectedBill] = useState(null)
    const [newGroupName, setNewGroupName] = useState('')
    const [editGroupId, setEditGroupId] = useState(null)
    const [editGroupName, setEditGroupName] = useState('')
    const [newCodeName, setNewCodeName] = useState('')

    // Priorities
    const [priorities, setPriorities] = useState([])
    const [selectedPriority, setSelectedPriority] = useState(null)
    const [priorityForm, setPriorityForm] = useState({ name: '', color: '#ff9800', mobile: false })
    const [newPriorityName, setNewPriorityName] = useState('')
    const [priorityPricing, setPriorityPricing] = useState([])

    // Priority pricing dialog
    const [priorityPricingOpen, setPriorityPricingOpen] = useState(false)
    const [priorityPricingTarget, setPriorityPricingTarget] = useState(null)
    const [newPriorityPricing, setNewPriorityPricing] = useState({ effectiveDate: null, defaultPrice: '', weekendPrice: '', groupPricing: [] })

    // Pricing dialog
    const [pricingOpen, setPricingOpen] = useState(false)
    const [pricingBilling, setPricingBilling] = useState(null)
    const [newPricing, setNewPricing] = useState({ effectiveDate: null, defaultPrice: '', groupPricing: [] })

    const navigate = useNavigate()
    const orgId = access?.org?.id

    const loadData = useCallback(() => {
        if (!orgId) return
        GetBillingGroups(orgId).then(setGroups).catch(() => {})
        GetOrgBilling(orgId).then(setBilling).catch(() => {})
        GetBillingPricing(orgId).then(setPricing).catch(() => {})
        GetPriorities(orgId).then(setPriorities).catch(() => {})
        GetPriorityPricing(orgId).then(setPriorityPricing).catch(() => {})
    }, [orgId])

    useEffect(() => {
        if (!access?.admin) navigate('/', { replace: true })
        else loadData()
    }, [access, navigate, loadData])

    // --- Groups ---
    const handleAddGroup = () => {
        const name = newGroupName.trim()
        if (!name) return
        CreateBillingGroup(orgId, name).then(g => { setGroups(prev => [...prev, g]); setNewGroupName('') }).catch(() => {})
    }
    const handleSaveGroupEdit = () => {
        if (!editGroupId || !editGroupName.trim()) return
        UpdateBillingGroup(editGroupId, editGroupName.trim()).then(g => {
            setGroups(prev => prev.map(x => x.id === g.id ? g : x)); setEditGroupId(null)
        }).catch(() => {})
    }
    const handleDeleteGroup = id => DeleteBillingGroup(id).then(() => setGroups(prev => prev.filter(x => x.id !== id))).catch(() => {})

    // --- Billing Codes ---
    const handleAddCode = () => {
        const name = newCodeName.trim()
        if (!name) return
        CreateBillingCode(orgId, name).then(r => { setBilling(prev => [...prev, r]); setNewCodeName('') }).catch(() => {})
    }
    const handleDeleteCode = id => {
        DeleteBillingCode(orgId, id).then(() => {
            setBilling(prev => prev.filter(b => b.id !== id))
            setPricing(prev => prev.filter(p => p.billing?.id !== id))
            if (selectedBill?.id === id) setSelectedBill(null)
        }).catch(() => {})
    }

    // Drag reorder
    const handleDrop = (e, index) => {
        e.preventDefault()
        const dragIndex = parseInt(e.dataTransfer.getData('text/plain'))
        if (dragIndex === index) return
        const newBilling = [...billing]
        const [item] = newBilling.splice(dragIndex, 1)
        newBilling.splice(index, 0, item)
        setBilling(newBilling)
        UpdateOrgBillingOrder(orgId, newBilling.map(b => b.id)).catch(() => {})
    }

    // --- Pricing ---
    const openPricingDialog = code => {
        setPricingBilling(code)
        setNewPricing({ effectiveDate: new Date(), defaultPrice: '', groupPricing: groups.map(g => ({ group: g.id, price: '' })) })
        setPricingOpen(true)
    }
    const handleAddPricing = () => {
        if (!newPricing.effectiveDate || !pricingBilling) return
        CreateBillingPricing(orgId, {
            billingId: pricingBilling.id,
            effectiveDate: newPricing.effectiveDate,
            defaultPrice: newPricing.defaultPrice !== '' ? parseFloat(newPricing.defaultPrice) || 0 : null,
            groupPricing: newPricing.groupPricing.filter(gp => gp.price !== '' && gp.price != null).map(gp => ({ ...gp, price: parseFloat(gp.price) || 0 }))
        }).then(() => { setPricingOpen(false); loadData() }).catch(() => {})
    }
    const handleDeletePricing = id => DeleteBillingPricing(id).then(() => setPricing(prev => prev.filter(p => p.id !== id))).catch(() => {})

    const pricingForCode = useCallback(billingId => pricing.filter(p => p.billing?.id === billingId).sort((a, b) => new Date(a.effectiveDate) - new Date(b.effectiveDate)), [pricing])

    // --- Priorities ---
    const handleAddPriority = () => {
        const name = newPriorityName.trim()
        if (!name) return
        const isMobile = priorityForm.mobile
        const sortOrder = isMobile
            ? (priorities.length ? Math.min(...priorities.map(p => p.sortOrder || 0)) - 1 : 0)
            : (priorities.length ? Math.max(...priorities.map(p => p.sortOrder || 0)) + 1 : 0)
        CreatePriority(orgId, { name, color: '#ff9800', price: 0, sortOrder, mobile: isMobile })
            .then(r => {
                const updated = isMobile ? [r, ...priorities] : [...priorities, r]
                const normalized = updated.map((p, i) => ({ ...p, sortOrder: i }))
                setPriorities(normalized)
                ReorderPriorities(orgId, normalized.map(p => p.id)).catch(() => {})
                setNewPriorityName('')
                setPriorityForm(prev => ({ ...prev, mobile: false }))
                selectPriority(r)
            }).catch(() => {})
    }
    const handleUpdatePriority = () => {
        if (!selectedPriority) return
        UpdatePriority(selectedPriority.id, priorityForm)
            .then(r => { setPriorities(prev => prev.map(x => x.id === r.id ? r : x)); setSelectedPriority(null) }).catch(() => {})
    }
    const handleDeletePriority = id => {
        DeletePriority(id).then(() => { setPriorities(prev => prev.filter(x => x.id !== id)); if (selectedPriority?.id === id) setSelectedPriority(null) }).catch(() => {})
    }
    const selectPriority = p => {
        setSelectedPriority(p)
        setPriorityForm({ name: p.name, color: p.color || '#ff9800', mobile: !!p.mobile })
    }
    const handlePriorityDrop = (e, index) => {
        e.preventDefault()
        const dragIndex = parseInt(e.dataTransfer.getData('text/plain'))
        if (dragIndex === index) return
        const reordered = [...priorities]
        const [item] = reordered.splice(dragIndex, 1)
        reordered.splice(index, 0, item)
        setPriorities(reordered)
        ReorderPriorities(orgId, reordered.map(p => p.id)).catch(() => {})
    }

    // --- Priority Pricing ---
    const openPriorityPricingDialog = p => {
        setPriorityPricingTarget(p)
        setNewPriorityPricing({ effectiveDate: new Date(), defaultPrice: '', weekendPrice: '', groupPricing: groups.map(g => ({ group: g.id, price: '', weekendPrice: '' })) })
        setPriorityPricingOpen(true)
    }
    const handleAddPriorityPricing = () => {
        if (!newPriorityPricing.effectiveDate || !priorityPricingTarget) return
        CreatePriorityPricing(orgId, {
            priorityId: priorityPricingTarget.id,
            effectiveDate: newPriorityPricing.effectiveDate,
            defaultPrice: newPriorityPricing.defaultPrice !== '' ? parseFloat(newPriorityPricing.defaultPrice) || 0 : null,
            weekendPrice: newPriorityPricing.weekendPrice !== '' ? parseFloat(newPriorityPricing.weekendPrice) || 0 : null,
            groupPricing: newPriorityPricing.groupPricing.filter(gp => (gp.price !== '' && gp.price != null) || (gp.weekendPrice !== '' && gp.weekendPrice != null)).map(gp => ({ ...gp, price: parseFloat(gp.price) || 0, weekendPrice: parseFloat(gp.weekendPrice) || 0 }))
        }).then(() => { setPriorityPricingOpen(false); loadData() }).catch(() => {})
    }
    const handleDeletePriorityPricing = id => DeletePriorityPricing(id).then(() => setPriorityPricing(prev => prev.filter(p => p.id !== id))).catch(() => {})
    const priorityPricingForCode = useCallback(priorityId => priorityPricing.filter(p => p.priority?.id === priorityId).sort((a, b) => new Date(a.effectiveDate) - new Date(b.effectiveDate)), [priorityPricing])

    if (!access?.admin) return null

    return (
        <Container>
            {/* Billing Groups */}
            <Grid container sx={{ mb: 4 }} justifyContent="left" maxWidth="sm">
                <Typography component="h1" variant="h4">Billing Groups</Typography>
                <FormControl fullWidth variant="standard">
                    <List sx={{ maxHeight: 200, overflow: "auto" }}>
                        {groups.map(g => (
                            <ListItem key={g.id} secondaryAction={
                                <>
                                    <IconButton size="small" onClick={() => { setEditGroupId(g.id); setEditGroupName(g.name) }}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => handleDeleteGroup(g.id)}><DeleteIcon fontSize="small" /></IconButton>
                                </>
                            }>
                                {editGroupId === g.id ? (
                                    <TextField size="small" value={editGroupName} onChange={e => setEditGroupName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleSaveGroupEdit()} onBlur={handleSaveGroupEdit} autoFocus />
                                ) : <ListItemText primary={g.name} />}
                            </ListItem>
                        ))}
                    </List>
                    <TextField size="small" label="New Group" value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAddGroup()} />
                    <Button onClick={handleAddGroup} startIcon={<AddIcon />}>Add Group</Button>
                </FormControl>
            </Grid>

            {/* Billing Codes + Pricing */}
            <Grid container sx={{ mb: 4 }} justifyContent="left" maxWidth="md">
                <Typography component="h1" variant="h4">Billing Codes</Typography>
                <FormControl fullWidth variant="standard">
                    <List sx={{ maxHeight: 400, overflow: "auto" }}>
                        {billing.map((item, index) => (
                            <ListItem key={item.id} disablePadding draggable
                                onDragStart={e => { e.dataTransfer.setData('text/plain', index); const img = document.createElement('div'); img.textContent = item.name; img.style.cssText = 'position:absolute;top:-1000px;background:#121212;padding:8px;border:1px solid #ccc;border-radius:4px;'; document.body.appendChild(img); e.dataTransfer.setDragImage(img, 0, 0); setTimeout(() => document.body.removeChild(img), 0) }}
                                onDragOver={e => e.preventDefault()} onDrop={e => handleDrop(e, index)}
                                sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
                                secondaryAction={selectedBill?.id === item.id && (
                                    <>
                                        <IconButton size="small" onClick={() => openPricingDialog(item)}><PostAddIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteCode(item.id)}><DeleteIcon fontSize="small" /></IconButton>
                                    </>
                                )}
                            >
                                <ListItemButton onClick={() => setSelectedBill(item)} selected={selectedBill?.id === item.id}>
                                    <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                    <ListItemText primary={item.name} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                    <TextField size="small" label="New Billing Code" value={newCodeName} onChange={e => setNewCodeName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAddCode()} />
                    <Button onClick={handleAddCode} startIcon={<AddIcon />}>Add Code</Button>
                </FormControl>

                {/* Pricing table for selected code */}
                {selectedBill && (
                    <Grid container sx={{ mt: 2 }}>
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Pricing for {selectedBill.name}</Typography>
                            <Button size="small" onClick={() => openPricingDialog(selectedBill)} startIcon={<PostAddIcon />}>Add Pricing</Button>
                        </Grid>
                        {pricingForCode(selectedBill.id).length > 0 ? (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Effective Date</TableCell>
                                        <TableCell>Default</TableCell>
                                        {groups.map(g => <TableCell key={g.id}>{g.name}</TableCell>)}
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pricingForCode(selectedBill.id).map(p => (
                                        <TableRow key={p.id} sx={new Date(p.effectiveDate) <= new Date() ? { bgcolor: 'action.selected' } : {}}>
                                            <TableCell>{new Date(p.effectiveDate).toLocaleDateString()}</TableCell>
                                            <TableCell>${(p.defaultPrice || 0).toFixed(2)}</TableCell>
                                            {groups.map(g => {
                                                const gp = p.groupPricing?.find(x => (x.group?.id || x.group) === g.id)
                                                return <TableCell key={g.id}>{gp ? `$${gp.price.toFixed(2)}` : '-'}</TableCell>
                                            })}
                                            <TableCell><IconButton size="small" onClick={() => handleDeletePricing(p.id)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, width: '100%' }}>No pricing configured</Typography>
                        )}
                    </Grid>
                )}
            </Grid>

            {/* Priorities */}
            <Grid container sx={{ mb: 4 }} justifyContent="left" maxWidth="md">
                <Typography component="h1" variant="h4">Priorities</Typography>
                <FormControl fullWidth variant="standard">
                    <List sx={{ maxHeight: 300, overflow: "auto" }}>
                        {priorities.map((p, index) => (
                            <ListItem key={p.id} disablePadding draggable
                                onDragStart={e => { e.dataTransfer.setData('text/plain', index); const img = document.createElement('div'); img.textContent = p.name; img.style.cssText = 'position:absolute;top:-1000px;background:#121212;padding:8px;border:1px solid #ccc;border-radius:4px;'; document.body.appendChild(img); e.dataTransfer.setDragImage(img, 0, 0); setTimeout(() => document.body.removeChild(img), 0) }}
                                onDragOver={e => e.preventDefault()} onDrop={e => handlePriorityDrop(e, index)}
                                sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
                                secondaryAction={selectedPriority?.id === p.id && (
                                    <>
                                        <IconButton size="small" onClick={() => openPriorityPricingDialog(p)}><PostAddIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={() => handleDeletePriority(p.id)}><DeleteIcon fontSize="small" /></IconButton>
                                    </>
                                )}
                            >
                                <ListItemButton onClick={() => selectPriority(p)} selected={selectedPriority?.id === p.id}>
                                    <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                    <span style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: (p.color || '#ff9800') + '33', display: 'inline-block', marginRight: 8, flexShrink: 0 }} />
                                    <ListItemText primary={p.name} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                        <TextField size="small" label="New Priority" value={newPriorityName}
                            onChange={e => setNewPriorityName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleAddPriority()}
                            sx={{ flex: 1 }} />
                        <FormControlLabel control={<Checkbox size="small" checked={priorityForm.mobile} onChange={e => setPriorityForm(prev => ({ ...prev, mobile: e.target.checked }))} />} label="Mobile" />
                    </Stack>
                    <Button onClick={handleAddPriority} startIcon={<AddIcon />}>Add Priority</Button>
                </FormControl>
                {selectedPriority && (
                    <FormControl fullWidth variant="standard" sx={{ mt: 3 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>Edit: {selectedPriority.name}</Typography>
                        <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                <label style={{ width: 40, height: 40, borderRadius: 4, backgroundColor: priorityForm.color + '33', display: 'block', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.2)' }}>
                                    <input type="color" value={priorityForm.color}
                                        onChange={e => setPriorityForm(prev => ({ ...prev, color: e.target.value }))}
                                        style={{ opacity: 0, width: 0, height: 0 }} />
                                </label>
                            </Grid>
                            <Grid item xs={10}>
                                <TextField size="small" label="Name" value={priorityForm.name}
                                    onChange={e => setPriorityForm(prev => ({ ...prev, name: e.target.value }))} fullWidth />
                            </Grid>
                        </Grid>
                        <FormControlLabel control={<Checkbox checked={priorityForm.mobile} onChange={e => setPriorityForm(prev => ({ ...prev, mobile: e.target.checked }))} />} label="Mobile" />
                        <Grid container spacing={1}>
                            <Button onClick={handleUpdatePriority} startIcon={<EditIcon />}>Save</Button>
                            <Button onClick={() => setSelectedPriority(null)}>Cancel</Button>
                        </Grid>
                        <Grid container sx={{ mt: 2 }} justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1">Pricing</Typography>
                            <Button size="small" onClick={() => openPriorityPricingDialog(selectedPriority)} startIcon={<PostAddIcon />}>Add Pricing</Button>
                        </Grid>
                        {priorityPricingForCode(selectedPriority.id).length > 0 ? (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Effective</TableCell>
                                        <TableCell>Weekday</TableCell>
                                        <TableCell>Weekend</TableCell>
                                        {groups.map(g => <TableCell key={g.id}>{g.name} WD</TableCell>)}
                                        {groups.map(g => <TableCell key={g.id + 'we'}>{g.name} WE</TableCell>)}
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {priorityPricingForCode(selectedPriority.id).map(p => (
                                        <TableRow key={p.id} sx={new Date(p.effectiveDate) <= new Date() ? { bgcolor: 'action.selected' } : {}}>
                                            <TableCell>{new Date(p.effectiveDate).toLocaleDateString()}</TableCell>
                                            <TableCell>${(p.defaultPrice || 0).toFixed(2)}</TableCell>
                                            <TableCell>${(p.weekendPrice || 0).toFixed(2)}</TableCell>
                                            {groups.map(g => {
                                                const gp = p.groupPricing?.find(x => (x.group?.id || x.group) === g.id)
                                                return <TableCell key={g.id}>{gp ? `$${gp.price.toFixed(2)}` : '-'}</TableCell>
                                            })}
                                            {groups.map(g => {
                                                const gp = p.groupPricing?.find(x => (x.group?.id || x.group) === g.id)
                                                return <TableCell key={g.id + 'we'}>{gp?.weekendPrice ? `$${gp.weekendPrice.toFixed(2)}` : '-'}</TableCell>
                                            })}
                                            <TableCell><IconButton size="small" onClick={() => handleDeletePriorityPricing(p.id)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, width: '100%' }}>No pricing configured</Typography>
                        )}
                    </FormControl>
                )}
            </Grid>

            {/* Pricing Dialog */}
            <Dialog open={pricingOpen} onClose={() => setPricingOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Pricing for {pricingBilling?.name}</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker label="Effective Date" sx={{ mt: 1, width: '100%' }}
                            value={newPricing.effectiveDate ? dayjs(newPricing.effectiveDate) : null}
                            onChange={val => val?.$d && setNewPricing(prev => ({ ...prev, effectiveDate: val.$d }))} />
                    </LocalizationProvider>
                    <TextField fullWidth sx={{ mt: 2 }} label="Default Price" type="number" value={newPricing.defaultPrice}
                        onChange={e => setNewPricing(prev => ({ ...prev, defaultPrice: e.target.value }))}
                        placeholder="Uses default"
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                    {groups.map((g, i) => (
                        <TextField key={g.id} fullWidth sx={{ mt: 1 }} label={`${g.name} Price`} type="number"
                            value={newPricing.groupPricing[i]?.price ?? ''}
                            onChange={e => setNewPricing(prev => {
                                const gp = [...prev.groupPricing]; gp[i] = { ...gp[i], price: e.target.value }; return { ...prev, groupPricing: gp }
                            })}
                            placeholder="Uses default"
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPricingOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddPricing} disabled={!newPricing.effectiveDate}>Add</Button>
                </DialogActions>
            </Dialog>

            {/* Priority Pricing Dialog */}
            <Dialog open={priorityPricingOpen} onClose={() => setPriorityPricingOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Pricing for {priorityPricingTarget?.name}</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker label="Effective Date" sx={{ mt: 1, width: '100%' }}
                            value={newPriorityPricing.effectiveDate ? dayjs(newPriorityPricing.effectiveDate) : null}
                            onChange={val => val?.$d && setNewPriorityPricing(prev => ({ ...prev, effectiveDate: val.$d }))} />
                    </LocalizationProvider>
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <TextField fullWidth label="Weekday Price" type="number" value={newPriorityPricing.defaultPrice}
                            onChange={e => setNewPriorityPricing(prev => ({ ...prev, defaultPrice: e.target.value }))}
                            placeholder="Uses default"
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                        <TextField fullWidth label="Weekend Price" type="number" value={newPriorityPricing.weekendPrice}
                            onChange={e => setNewPriorityPricing(prev => ({ ...prev, weekendPrice: e.target.value }))}
                            placeholder="Uses default"
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                    </Stack>
                    {groups.map((g, i) => (
                        <Stack direction="row" spacing={2} sx={{ mt: 1 }} key={g.id}>
                            <TextField fullWidth label={`${g.name} Weekday`} type="number"
                                value={newPriorityPricing.groupPricing[i]?.price ?? ''}
                                onChange={e => setNewPriorityPricing(prev => {
                                    const gp = [...prev.groupPricing]; gp[i] = { ...gp[i], price: e.target.value }; return { ...prev, groupPricing: gp }
                                })}
                                placeholder="Uses default"
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                            <TextField fullWidth label={`${g.name} Weekend`} type="number"
                                value={newPriorityPricing.groupPricing[i]?.weekendPrice ?? ''}
                                onChange={e => setNewPriorityPricing(prev => {
                                    const gp = [...prev.groupPricing]; gp[i] = { ...gp[i], weekendPrice: e.target.value }; return { ...prev, groupPricing: gp }
                                })}
                                placeholder="Uses default"
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                        </Stack>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPriorityPricingOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddPriorityPricing} disabled={!newPriorityPricing.effectiveDate}>Add</Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

export default BillingSettings
